import { Box, Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core'
import type { FileWithPath } from '@mantine/dropzone'
import { notifications } from '@mantine/notifications'
import { IconDeviceFloppy } from '@tabler/icons-react'
import type { MRT_ColumnDef } from 'mantine-react-table'
import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'

import { AIInputWrapper } from '@/components/AIInputWrapper'
import {
  acceptedFileUploadText,
  allUploadMimeTypes,
  FileUpload,
  needsExtractionMimeTypes,
  rejectedFileUploadText,
  supportsProgressMimeTypes,
} from '@/components/FileUpload'
import { Table } from '@/components/Table'
import { useAIFieldGeneration } from '@/hooks/useAIFieldGeneration'
import { useFileUpload } from '@/hooks/useFileUpload'
import { useGlobalState } from '@/hooks/useGlobalState'
import { AddStepDataContentModal } from '@/screens/ContentModuleFormScreen/AddStepDataContentModal'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { FAQSection, type FAQSectionRef } from '@/screens/ContentModuleFormScreen/FAQSection'
import { ResourcesTable } from '@/screens/ContentModuleFormScreen/ResourcesTable'
import type { CreateStepParams, GetContentResponse, ProcessProgress } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'
import * as classes from '@/styles/ContentModuleFormScreen.css'
import { castStepToZodForm, castZodFormToStep } from '@/utils/contentModuleForm'
import { getRequiredFieldDescriptionProps } from '@/utils/forms'

import { ExtractFromWebsiteModal } from './ExtractFromWebsiteModal'

export const StepModal = observer(function StepModal(props: CMFormSection) {
  const { uiStore, api } = useGlobalState()

  // State for ExtractFromWebsiteModal
  const [extractModalOpened, setExtractModalOpened] = useState(false)
  const [isExtracting, setIsExtracting] = useState(false)

  // State to track processing of any uploaded content
  const [processing, setProcessing] = useState<boolean>(false)
  const [processProgressPct, setProcessProgressPct] = useState<number>(0)

  // AbortController for step creation
  const [stepCreationAbortController, setStepCreationAbortController] = useState<AbortController | null>(null)

  // Ref to track whether we've already triggered auto-generation for this step
  const autoGenerationTriggeredRef = useRef<string | null>(null)

  // Ref for FAQ section to access save functionality
  const faqSectionRef = useRef<FAQSectionRef>(null)

  const {
    uploading: contentUploading,
    upload: uploadContent,
    cancel: cancelContentUpload,
  } = useFileUpload({
    onPrepareError: () =>
      props.stepForm.setFieldError('content.file', 'Something went wrong. Please try again or contact support.'),
    onUploadError: () =>
      props.stepForm.setFieldError('content.file', 'Upload failed. Please try again or contact support.'),
    onUploadSuccess: () => props.stepForm.setFieldError('content.file', undefined),
  })

  function openDataContentModal() {
    props.stepForm.setFieldValue('content.text', props.stepContentQuery.data?.content ?? '')

    props.openModal?.({
      title: `Edit File Content Manually`,
      subtitle: 'Copy and paste or enter your content manually.',
      Component: AddStepDataContentModal,
      ModalProps: {
        size: '95vw',
      },
    })
  }

  function openExtractFromWebsiteModal() {
    setExtractModalOpened(true)
  }

  async function handleExtractFromWebsiteSubmit(values: { url: string }) {
    setExtractModalOpened(false)
    setIsExtracting(true)
    setProcessing(true)
    setProcessProgressPct(50)

    try {
      if (props.stepId) {
        // Editing existing section - use content update
        await props.contentUpdateMutation?.mutateAsync({
          step_id: props.stepId,
          url: values.url,
          use_llm_for_scraping: false,
        })
      } else {
        // Creating new section - use step create
        const lastStep = props.stepsQuery.data?.[props.stepsQuery.data.length - 1]
        const params: CreateStepParams = {
          task_id: props.taskId!,
          url: values.url,
          use_llm_for_scraping: false,
          preceding_step_id: lastStep?.stepId,
        }
        await createStepWithAbortSupport(params)
        uiStore.setPendingStepDeletion(true)
      }
    } catch (error) {
      console.error('Error extracting from website:', error)
      notifications.show({
        title: 'Extraction Failed',
        message: 'Could not start content extraction. Please check the URL or try again later.',
        color: 'red',
      })
    } finally {
      setIsExtracting(false)
      setProcessing(false)
      setProcessProgressPct(0)
    }
  }

  function onProgress(progress: ProcessProgress['processProgress']) {
    const current = progress?.current ?? 0
    const total = progress?.total ?? 0
    // Avoid NaN on divide by zero
    const fraction = current / total || 0
    const progressPct = fraction * 100

    console.log(`StepModal:onProgress ${progressPct}`)
    setProcessProgressPct(progressPct)
  }

  // Helper function to create step with abort controller support
  async function createStepWithAbortSupport(params: CreateStepParams) {
    const abortController = new AbortController()
    setStepCreationAbortController(abortController)

    try {
      // Use the API directly instead of the mutation to pass the signal
      const response = await api.createStep(params, abortController.signal)

      // Manually trigger the success side effects that the mutation would normally handle
      notifications.show({
        title: 'File Created',
        message: 'The file was successfully created.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })

      const steps = await props.stepsQuery.refetch()
      const step = steps.data?.find(step => step.stepId === response.stepId)

      if (step) {
        props.stepForm.setValues(castStepToZodForm(step))
      }

      return response
    } catch (error) {
      if (abortController.signal.aborted) {
        console.log('[StepModal] File creation was cancelled')
        return null
      }

      // Handle other errors
      notifications.show({
        title: 'Something went wrong.',
        message: 'There was an issue creating the file. Please try again or contact support.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      throw error
    } finally {
      setStepCreationAbortController(null)
    }
  }

  async function processDroppedFiles(files: FileWithPath[]) {
    const file = files[0]

    const uploadResponse = await uploadContent(file)

    if (!uploadResponse) return

    // All files (text, PDFs, videos, images) go through the same extraction process
    if (props.stepId) {
      if (needsExtractionMimeTypes.includes(file.type)) {
        setProcessing(true)
        setProcessProgressPct(0)

        if (supportsProgressMimeTypes.includes(file.type)) {
          await props.contentUpdateMutation?.mutateAsync({
            step_id: props.stepId!,
            file_name: file.name,
            s3_file: uploadResponse.fileName,
            onProgress,
          })
        } else {
          // We fake our processing progress for filetypes that don't support progress feedback
          // in the processing backend
          setProcessProgressPct(50)
          await props.contentUpdateMutation?.mutateAsync({
            step_id: props.stepId!,
            file_name: file.name,
            s3_file: uploadResponse.fileName,
          })
        }

        setProcessing(false)
      } else {
        await props.contentUpdateMutation?.mutateAsync({ step_id: props.stepId!, file })
      }
    } else {
      const lastStep = props.stepsQuery.data?.[props.stepsQuery.data.length - 1]

      if (needsExtractionMimeTypes.includes(file.type)) {
        setProcessing(true)
        setProcessProgressPct(0)

        if (supportsProgressMimeTypes.includes(file.type)) {
          await createStepWithAbortSupport({
            task_id: props.taskId!,
            file_name: file.name,
            s3_file: uploadResponse.fileName,
            onProgress,
          })
        } else {
          // We fake our processing progress for filetypes that don't support progress feedback
          // in the processing backend
          setProcessProgressPct(50)
          await createStepWithAbortSupport({
            task_id: props.taskId!,
            file_name: file.name,
            s3_file: uploadResponse.fileName,
          })
        }

        setProcessing(false)
      } else {
        await createStepWithAbortSupport({
          task_id: props.taskId!,
          preceding_step_id: lastStep?.stepId,
          file,
        })
      }
      uiStore.setPendingStepDeletion(true)
    }
  }

  function cancelStepCreation() {
    if (stepCreationAbortController) {
      console.log('[StepModal] Cancelling step creation...')
      stepCreationAbortController.abort()
      setStepCreationAbortController(null)
    }
  }

  async function updateStep() {
    const validate = props.stepForm.validateField

    const validations = [validate('name')]

    if (validations.some(v => v.hasError)) {
      notifications.show({
        title: 'Could Not Submit Form',
        message: 'There was an issue submitting file details. Please check the form for errors.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })

      return
    }

    try {
      // First, validate and save FAQs - this will throw if there are validation errors
      await faqSectionRef.current?.saveUnsavedFAQs()

      // Only proceed with step update if FAQ validation passed
      await new Promise((resolve, reject) => {
        props.stepUpdateMutation.mutate(
          {
            ...castZodFormToStep(props.stepForm.getValues()),
            step_id: props.stepId!,
          },
          {
            onSuccess: () => {
              props.closeModal(() => {
                props.stepForm.setValues(castStepToZodForm())
                props.faqActions.resetStepFAQState()
              })
              resolve(undefined)
            },
            onError: reject,
          },
        )
      })
    } catch (error) {
      console.error('Failed to save step:', error)

      if (error instanceof Error && error.message === 'FAQ_VALIDATION_ERROR') {
        return // Keep the modal open, validation errors are already shown
      }

      notifications.show({
        title: 'Could Not Save Step',
        message: 'There was an issue saving your step and FAQs. Please try again.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      return
    }
  }

  const { getFieldState, generateFieldByName, regenerateFieldByName, acceptStepSuggestion, rejectSuggestion } =
    useAIFieldGeneration(props)

  useEffect(() => {
    props.stepForm.clearErrors()
    // Reset auto-generation tracking when stepId changes
    autoGenerationTriggeredRef.current = null
  }, [props.stepId])

  // Auto-generate AI suggestions when content is available AND processing is complete
  useEffect(() => {
    const isStepCreated = props.cmQuery.isSuccess && props.stepsQuery.isSuccess
    const hasContent = !!props.stepContentQuery.data
    const currentName = props.stepForm.getValues().name
    const shouldTrigger = (props.contentUpdateMutation.isSuccess || isStepCreated) && !processing

    // Create a unique key for this step+content combination
    const generationKey = `${props.stepId}-${!!props.stepContentQuery.data}`

    // Auto-generate AI suggestion for name field when:
    // 1. We have a stepId and content is available
    // 2. Name field is empty
    // 3. This is triggered by content update or step creation
    // 4. Content processing is complete (not currently processing)
    // 5. We haven't already triggered auto-generation for this step+content combination
    if (
      props.stepId &&
      hasContent &&
      !currentName &&
      shouldTrigger &&
      autoGenerationTriggeredRef.current !== generationKey
    ) {
      autoGenerationTriggeredRef.current = generationKey
      generateFieldByName('step', 'name')
    }
  }, [
    props.stepId,
    props.cmQuery.isSuccess,
    props.stepsQuery.isSuccess,
    props.stepCreateMutation.isSuccess,
    props.contentUpdateMutation.isSuccess,
    !!props.stepContentQuery.data,
    processing, // Added - watch for processing completion
    generateFieldByName,
    // Watch for form changes to detect when name field becomes empty
    props.stepForm.getValues().name,
  ])

  const isStepCreating = !!stepCreationAbortController
  const isSectionContentMutating =
    contentUploading || props.stepCreateMutation.isPending || props.contentUpdateMutation.isPending || isStepCreating

  const hasContent = !!props.stepContentQuery.data
  const isMutating = props.stepUpdateMutation.isPending || isSectionContentMutating || isExtracting
  const contentLoading = contentUploading || processing || isSectionContentMutating || props.stepContentQuery.isFetching

  // Use unified upload mime types for all file types
  const allAcceptedMimeTypes = allUploadMimeTypes

  return (
    <>
      <ExtractFromWebsiteModal
        opened={extractModalOpened}
        onClose={() => {
          setExtractModalOpened(false)
          if (isExtracting) {
            setIsExtracting(false)
            setProcessing(false)
            setProcessProgressPct(0)
          }
        }}
        onSubmit={handleExtractFromWebsiteSubmit}
        isExtracting={isExtracting}
      />
      <Box className={commonClasses.stickyModalFooterContentWrapper}>
        <FileUpload
          withAsterisk
          label="File"
          activateOnClick={false}
          loading={contentLoading}
          processing={processing}
          processProgress={processProgressPct}
          onDrop={processDroppedFiles}
          error={props.stepForm.getInputProps('content.file').error}
          titles={{
            idle: `${hasContent ? 'Replace' : 'Add'} File`,
            reject: rejectedFileUploadText,
          }}
          accept={allAcceptedMimeTypes}
          maxFiles={1}
          primaryActionLabel={`${hasContent ? 'Update' : 'Upload'} File`}
          secondaryActionLabel={`${hasContent ? 'Edit' : 'Add'} Manually`}
          tertiaryActionLabel="Extract from Website"
          onSecondaryActionClick={openDataContentModal}
          onTertiaryActionClick={openExtractFromWebsiteModal}
          hints={{
            idle: (
              <Text component="span" style={{ whiteSpace: 'pre-line' }}>
                {acceptedFileUploadText}
              </Text>
            ),
          }}
          TopSlot={
            hasContent && (
              <Table
                data={props.stepContentQuery.data ? [props.stepContentQuery.data!] : []}
                columns={contentCols}
                rowActionsInclude={['edit']}
                enableTopToolbar={false}
                mantinePaperProps={{ className: classes.fileUploadTable }}
                enableTableHead={false}
                onRowActionClick={action => {
                  if (action === 'edit') {
                    openDataContentModal()
                  }
                }}
                displayColumnDefOptions={{ 'mrt-row-actions': { size: 60 } }}
              />
            )
          }
        />
        {contentLoading && (
          <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} mt="sm">
            <Text c="dimmed" className={classes.inputDescription}>
              {contentUploading && 'Uploading content ...'}
              {processing && 'Processing content, this may take a few minutes...'}
              {!processing && !contentUploading && 'Loading content...'}
            </Text>
          </Box>
        )}

        {hasContent && (
          <>
            <Stack my="xxl">
              <AIInputWrapper
                fieldState={getFieldState('name')}
                onAskSidekick={() => generateFieldByName('step', 'name')}
                onAccept={() => acceptStepSuggestion('name')}
                onReject={() => rejectSuggestion('name')}
                onRegenerate={() => regenerateFieldByName('step', 'name')}>
                <TextInput
                  withAsterisk
                  label="File Title"
                  {...props.stepForm.getInputProps('name')}
                  {...getRequiredFieldDescriptionProps(props.stepForm, 'name', 'Name of your file.')}
                />
              </AIInputWrapper>
            </Stack>

            {/* <Grid.Col span={12}>
                {props.stepForm.getValues().titleImage ? (
                  <Box>
                    <InputLabel label="Section Header Image" />

                    <Indicator
                      color="red"
                      size={36}
                      withBorder
                      label={<IconTrash size={22} className={classes.tableRowIcon} stroke={1.5} />}
                      styles={{ indicator: { cursor: 'pointer' } }}
                      onClick={() => props.stepForm.setFieldValue('titleImage', null)}>
                      <Image
                        radius="md"
                        src={`${configStore.serverBaseUrl}/files/${props.stepForm.getValues().titleImage}`}
                        h="auto"
                        w="100%"
                      />
                    </Indicator>

                    <Text c="dimmed" className={classes.inputDescription}>
                      This image will be displayed when the section is first introduced. You can add more images in
                      supplemental resources.
                    </Text>
                  </Box>
                ) : (
                  <FileUpload
                    label="Section Header Image"
                    activateOnClick={false}
                    loading={titleImageUploading}
                    maxFiles={1}
                    error={props.stepForm.getInputProps('titleImage').error}
                    titles={{ idle: 'Upload Image' }}
                    accept={ALLOWED_IMAGE_TYPES}
                    primaryActionLabel="Select File"
                    onDrop={async files => {
                      const file = files[0]

                      const uploadResponse = await uploadTitleImage(file)

                      if (!uploadResponse) return

                      props.stepForm.setFieldValue('titleImage', uploadResponse.fileName)
                    }}
                    description="This image will be displayed when the section is first introduced. You can add more images in supplemental resources."
                    hint="Drag and drop image file here or click below to browse. File should be in .jpg, .png, or .gif formats."
                  />
                )}
              </Grid.Col> */}

            <FAQSection {...props} type="step" ref={faqSectionRef} />

            <ResourcesTable {...props} type="step" />
          </>
        )}
      </Box>

      <Modal.Header className={commonClasses.stickyModalFooter}>
        <Group gap="xl">
          {hasContent && (
            <Button
              leftSection={<IconDeviceFloppy />}
              onClick={() => {
                updateStep()
                uiStore.setPendingStepDeletion(false)
              }}
              loading={isMutating}>
              Save and Exit
            </Button>
          )}

          {(!isMutating || contentUploading || isStepCreating) && (
            <Button
              size="sm"
              variant="light"
              color="gray"
              onClick={() => {
                props.closeModal(() => {
                  const state = uiStore.pendingStepDeletion
                  props.stepForm.setValues(castStepToZodForm())
                  props.faqActions.resetStepFAQState()
                  if (state) {
                    props.stepDeleteMutation?.mutate({
                      step_id: props.stepId!,
                      task_id: props.taskId!,
                      showToast: false,
                    })
                  } else if (isStepCreating) {
                    cancelStepCreation()
                  } else {
                    cancelContentUpload()
                  }
                  uiStore.setPendingStepDeletion(false)
                })
              }}>
              Cancel
            </Button>
          )}
        </Group>
      </Modal.Header>
    </>
  )
})

const contentCols: MRT_ColumnDef<GetContentResponse>[] = [
  {
    id: 'content',
    header: '',
    grow: true,
    Cell: props => {
      return (
        <Text size="sm" fw={500} lineClamp={6} style={{ whiteSpace: 'pre-wrap' }}>
          {props.row.original?.content ?? ''}
        </Text>
      )
    },
  },
]

//     {/*
// // ARCHIVED: As per Courtney, this field is not needed.
// <Grid.Col span={6}>
//   <Textarea
//     withAsterisk
//     label="Section Overview"
//     description="This is how the Expert Sidekick will respond to inquiries about what the section covers. Max: 400 characters."
//     autosize
//     resize="vertical"
//     minRows={8}
//     maxLength={400}
//     rightSectionProps={{ className: commonStyles.textareaRightSectionWithAIActionButton }}
//     rightSection={
//       <AIActionButton
//         variant="action-button"
//         onClick={() =>
//           askSMESKForField({
//             type: 'step',
//             userChatFieldLabel: 'Section Overview',
//             formField: 'shortDescription',
//             apiParamField: 'short_description',
//             apiResponseField: 'shortDescription',
//             actions: ['accept', 'regenerate'],
//           })
//         }
//       />
//     }
//     {...props.form.getInputProps('shortDescription')}
//   />
// </Grid.Col> */}

//       {/*
// // ARCHIVED: As per Courtney, this field is not needed.
// <Grid.Col span={12}>
//   <Radio.Group
//     withAsterisk
//     label="Delivery Preference"
//     {...props.form.getInputProps('deliveryMode')}
//     value={String(props.form.getValues().step?.deliveryMode)}
//     onChange={e => {
//       props.form.setFieldValue('deliveryMode', Number(e) as z.infer<typeof props.stepSchema>['deliveryMode'])
//     }}>
//     <Grid gutter="xl">
//       <Grid.Col span={6}>
//         <RadioCard
//           value={String(props.stepSchema.shape.deliveryMode.enum.Paraphrase)}
//           label="Paraphrase"
//           description="When sharing content with users, Sidekick can paraphrase information (recommended)."
//         />
//       </Grid.Col>

//       <Grid.Col span={6}>
//         <RadioCard
//           value={String(props.stepSchema.shape.deliveryMode.enum.Quote)}
//           label="Quote"
//           description="When sharing content with users, Sidekick must only directly quote from the content files."
//         />
//       </Grid.Col>
//     </Grid>
//   </Radio.Group>
// </Grid.Col> */}

//         {/*
// // ARCHIVED: As per Courtney, this field is not needed.
// <Grid.Col span={12}>
//   <Radio.Group
//     label={<ComingSoon prefix="Privacy Setting " />}
//     description="This setting applies to this section only and will override the module privacy setting chosen in Step 3."
//     inputWrapperOrder={['label', 'description', 'input', 'error']}
//     {...props.form.getInputProps('access')}
//     onChange={e => {
//       props.form.setFieldValue('access', e as z.infer<typeof props.stepSchema>['access'])
//       // updateCM(['access'])() // NOT IMPLEMENTED
//     }}>
//     <Group gap="xl">
//       <RadioCard
//         value={props.stepSchema.shape.access.enum.open}
//         label="Open"
//         description="The user's Sidekick can sit in on the user's conversation about this section."
//       />

//       <RadioCard
//         value={props.stepSchema.shape.access.enum.restricted}
//         label="Restricted"
//         description="The section contains proprietary and confidential information."
//       />
//     </Group>
//   </Radio.Group>
// </Grid.Col> */}
